import { shortcuts, useParams } from '@tunasong/ui-lib'
import { features, useSelector, useThunkDispatch } from '@tunasong/redux'
import { isHotkey } from '@tunasong/models'
import React, { useEffect } from 'react'
import { useSearchDialog } from '@tunasong/search'
import { graphHooks } from '@tunasong/graph-lib/react'

/** "/" types a slash in a field or the editor; only elsewhere does it open search (#2555 M7) */
const isEditableTarget = (target: EventTarget | null) => {
  const element = target as { tagName?: string; isContentEditable?: boolean } | null
  return Boolean(element?.isContentEditable) || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element?.tagName ?? '')
}

/** An open dialog holds focus, so a "/" from inside one belongs to it, not to search */
const isInDialog = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest('[role="dialog"], [role="alertdialog"]'))

export const useGlobalHotkeys = () => {
  const dispatch = useThunkDispatch()

  const { entityId } = useParams()
  /**
   * This hook runs in the app container, outside AuthGuard. Loading the entity before the session is in the store
   * failed for want of a token, and the retry backoff held up the route's entity load by up to ~840 ms (GH #1543).
   */
  const isLoggedIn = useSelector(state => state.user.isLoggedIn)
  const { entity } = graphHooks.useEntity(isLoggedIn ? entityId : null)

  // Find the current entity, if any
  const { dialog, setOpen } = useSearchDialog(entity)

  useEffect(() => {
    const handleKey = (ev: KeyboardEvent) => {
      if (isHotkey(shortcuts.Save.hotkey)(ev)) {
        ev.preventDefault()
        dispatch(
          features.notifications.actions.setAlert({
            severity: 'success',
            message: 'Your document is saved automatically.',
          })
        )
      }
      if (isHotkey(shortcuts.Search.hotkey)(ev)) {
        ev.preventDefault()
        setOpen(true)
      }
      /**
       * By the character typed, not the key code: "/" is Shift+7 on a Norwegian keyboard, and is-hotkey matches codes.
       * Shift is allowed for that reason; Ctrl, Cmd and Alt are not.
       */
      const quickSearch = ev.key === shortcuts.QuickSearch.hotkey && !ev.ctrlKey && !ev.metaKey && !ev.altKey
      if (quickSearch && !isEditableTarget(ev.target) && !isInDialog(ev.target)) {
        ev.preventDefault()
        setOpen(true)
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('keydown', handleKey)
    }
  }, [dispatch, setOpen])

  return { globalHotkeyModals: [<React.Fragment key="search-dialog">{dialog}</React.Fragment>] }
}

export default useGlobalHotkeys
